<template>
  <div ref="rootElRef" class="video">
    <Transition name="g-fade">
      <div v-if="!isPlayingRef" class="video__poster">
        <button class="video__poster__button" @click="play">
          <Action
            class="video__poster__action"
            :content="{
              tag: 'button',
              title: 'Play',
              icon: 'play',
              modifiers: ['no-label'],
            }"
          />
        </button>

        <div class="video__poster__picture">
          <img
            v-if="content.picture"
            v-src="content.picture"
            :src="content.picture.src"
            sizes="(min-width: 1440px) 50vw, 70vw"
          />
          <img
            v-else
            :src="`https://img.youtube.com/vi/${content.id}/mqdefault.jpg`"
            :srcset="getSrcset()"
            sizes="(min-width: 1440px) 50vw, 70vw"
          />
        </div>

        <div v-if="durationRef" class="video__poster__duration">
          {{ durationRef }}
        </div>
      </div>
    </Transition>
    <slot name="overlay" class="video__overlay"></slot>

    <div
      v-if="content.id"
      :id="uuid"
      ref="playerElRef"
      :data-youtube-id="content.id"
      class="video__player vlite-js"
    ></div>
  </div>
</template>

<script lang="ts">
import type { Picture } from '@/types'

export interface Video {
  id: string
  picture?: Picture
}
</script>

<script setup lang="ts">
import { useDateFormat } from '@vueuse/core'
import { v4 as uuidv4 } from 'uuid'
import { onBeforeUnmount, onMounted, ref } from 'vue'

import type { PropType } from 'vue'

import 'vlitejs/dist/vlite.css'

// Props
const props = defineProps({
  content: {
    type: Object as PropType<Video>,
    required: true,
  },
})

// Refs
const isPlayingRef = ref(false)
const playerElRef = ref<HTMLElement | null>(null)
const playerRef = ref<InstanceType<typeof Vlitejs> | null>(null)
const durationRef = ref<string | null>(null)

// Variables
const uuid = uuidv4().replace(/(-).+/, '')

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let Vlitejs: any

// Emits
const emit = defineEmits(['play', 'pause', 'progress'])

// Methods
// Create srcset from youtube poster urls
const getSrcset = (): string => {
  const breakpoints: Record<string, string> = {
    320: 'mqdefault',
    480: 'hqdefault',
    640: 'sddefault',
    1280: 'maxresdefault',
  }
  const set: string[] = []

  Object.keys(breakpoints).forEach((breakpoint: string) => {
    set.push(
      `https://img.youtube.com/vi/${props.content.id}/${breakpoints[breakpoint]}.jpg ${breakpoint}w`
    )
  })

  return set.join(', ')
}

// Create vlite player
const initPlayer = () => {
  const options = {
    autoHide: true,
    bigPlay: false,
    fullscreen: true,
    providerParams: {
      modestbranding: 1,
      showinfo: 0,
    },
  }

  // eslint-disable-next-line no-new
  new Vlitejs(playerElRef.value, {
    options,
    provider: 'youtube',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onReady: (player: any) => {
      playerRef.value = player

      const duration = playerRef.value.instance.getDuration()
      durationRef.value =
        duration > 3600
          ? useDateFormat((duration - 3600) * 1000, 'HH:mm:ss').value
          : useDateFormat(duration * 1000, 'mm:ss').value

      // Register events
      playerRef.value.on('play', () => {
        emit('play', getCurrentTime())

        onPlay()
      })

      playerRef.value.on('pause', () => {
        emit('pause', getCurrentTime())
        onPause()
      })

      playerRef.value.on('ended', () => {
        isPlayingRef.value = false
      })
    },
  })
}

// Play video
const play = (event: MouseEvent, timestamp?: number) => {
  if (playerRef.value && !isPlayingRef.value) {
    // Set player time to given timestamp
    if (timestamp && typeof timestamp === 'number') {
      playerRef.value.seekTo(timestamp)
    }

    playerRef.value.play()
    playerRef.value.unMute()
    isPlayingRef.value = true
  }
}

// Event handling
// Pause video
const pause = () => {
  if (playerRef.value && isPlayingRef.value) {
    playerRef.value.pause()
    isPlayingRef.value = false
  }
}

// Get current time, in s
const getCurrentTime = (): number => playerRef.value.instance.getCurrentTime()

// On video pause (vlite event)
const onPause = () => (isPlayingRef.value = false)

// On video play (vlite event)
const onPlay = () => (isPlayingRef.value = true)

onMounted(async () => {
  Vlitejs = await import(
    /* webpackChunkName: 'vendor-vlitejs' */ 'vlitejs'
  ).then(module => module.default)
  initPlayer()
})

onBeforeUnmount(() => {
  playerRef.value && playerRef.value.destroy()
})

// Expose
defineExpose({ play, pause, getCurrentTime })
</script>

<style lang="scss" scoped>
@import '~/vlitejs/dist/vlite.css';

.video {
  @extend %br-default;

  position: relative;
  z-index: 0;
  overflow: hidden;
  aspect-ratio: 16/9;

  :deep(.action__icon) {
    --icon-size: 2.8rem;

    fill: $c-green-abr;
  }

  :deep(.v-vlite) {
    /* stylelint-disable-next-line custom-property-pattern */
    --vlite-colorPrimary: var(--c-primary);

    font-family: $ff-default;
  }
}

.video__poster {
  @include get-all-space;

  z-index: 2;
  display: grid;
  place-items: center;
  overflow: hidden;
  transition: opacity 0.2s;
  transform: translateX(0);
}

.video__poster__button {
  @extend %button-nostyle;

  // prettier-ignore
  @include fluid(--padding, (m: 1.2rem, xl: 2rem));

  position: relative;
  z-index: 1;
  padding: var(--padding);
  transition: 0.2s $ease-out;
  cursor: pointer;

  &::after {
    @include center-xy;

    content: '';
    width: calc(100% + 20rem);
    height: calc(100% + 20rem);
  }

  &:hover,
  &:focus-visible {
    :deep(.action__icon) {
      &:nth-child(1) {
        opacity: 0;
        /* stylelint-disable-next-line declaration-no-important */
        transform: translateY(-130%) !important;
      }

      &:nth-child(2) {
        opacity: 1;
        /* stylelint-disable-next-line declaration-no-important */
        transform: translateY(0%) !important;
      }
    }
  }

  @include mq(l) {
    border-radius: 1.6rem;
  }
}

.video__poster__action {
  justify-content: center;
  align-items: center;
  width: 4.8rem;
  height: 4.8rem;
  padding: 0;
  background-color: $c-white;
  border-radius: 1.2rem;
}

.video__poster__picture {
  @include get-all-space;

  transition: 0.2s $ease-out;

  img {
    @include image-fit;
  }
}

.video__poster__duration {
  position: absolute;
  bottom: $spacing;
  left: $spacing;
  padding: 0.9rem 2rem;
  color: $c-black;
  background: $c-white;
  border-radius: 2rem;
}
</style>
